import { observer } from 'mobx-react-lite';
import { Alert, Button, Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { type NodeType } from 'redux/slices/lists/nodeTypesSlice';
import { type WorkspaceItem } from 'api/workspace/endpoints/getWorkspaceContents';
import { type WorkspaceModel } from './WorkspaceModel';
import { WorkspaceItemLabelFor } from './WorkspaceItemLabelFor';
import { WorkspaceRowActions } from './WorkspaceRowActions';

const TYPE_LABELS: Record<WorkspaceItem['type'], string> = {
  zone: 'Zone',
  folder: 'Folder',
  node_definition: 'Node definition',
  integration: 'Integration',
  event: 'Event',
  lock: 'Lock',
  trigger: 'Trigger',
  queue: 'Queue',
  scheduling_window: 'Scheduling window',
  recurrence_definition: 'Recurrence definition',
  report_definition: 'Report definition',
  monitoring_dashboard: 'Monitoring dashboard',
  python_library: 'Python library',
  table: 'Table',
  connection: 'Connection',
};

/**
 * The merged-contents pane: the selected container's child folders (first) then
 * its objects, in an Actions / Name / Type / Description table with a per-row
 * actions dropdown, a name tooltip, and a keyset "Load more". Pure observer. The
 * toolbar and the "showing X of Y" count live in WorkspaceToolbar.
 */
export const WorkspaceContents = observer(({ model, knownNodeTypes }: { readonly model: WorkspaceModel; readonly knownNodeTypes: NodeType[] }) => {
  const navigate = useNavigate();
  const { contents } = model;
  const createIn = model.createTarget ?? undefined;

  if (model.selected === null) {
    return <div className="text-body-tertiary p-4">Select a zone or folder to see its contents.</div>;
  }

  // Folders first (from the tree fetch), then the paginated objects.
  const rows = [...model.folderRows, ...contents.items];
  const containerLabel = model.selected.startsWith('zone:') ? 'Zone' : 'Folder';

  return (
    <div className="workspace-contents">
      {contents.error && (
        <Alert variant="danger" className="m-2">
          {contents.error}
        </Alert>
      )}

      {contents.loading ? (
        <div className="p-4 text-center">
          <Spinner animation="border" size="sm" />
        </div>
      ) : (
        <>
          <Table hover className="mb-0 workspace-contents-table">
            <thead>
              <tr>
                <th className="workspace-col-actions">Actions</th>
                <th>Name</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <tr key={`${item.type}:${item.id}`}>
                  <td className="workspace-col-actions">
                    <WorkspaceRowActions item={item} knownNodeTypes={knownNodeTypes} refresh={model.refresh} navigate={navigate} createIn={createIn} />
                  </td>
                  <td>
                    <WorkspaceItemLabelFor item={item} />
                  </td>
                  <td>{TYPE_LABELS[item.type]}</td>
                  {/* null (no description column) and '' (empty) both render as —. */}
                  <td className="text-body-tertiary">{item.description?.trim() ? item.description : '—'}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-body-tertiary text-center py-4">
                    This {containerLabel} is empty.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {contents.hasMore && (
            <div className="text-center p-2">
              <Button variant="outline-secondary" size="sm" onClick={() => void contents.loadMore()} disabled={contents.loadingMore}>
                {contents.loadingMore ? <Spinner animation="border" size="sm" /> : 'Load more'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
});
